import {
	ExareGdsServiceProductRefundInfo,
	ServicesRefundInfoFragment,
	IGdsServiceInfo,
	PassengerSelectedGdsServicesFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment
} from '@websky/graphql';
import { RefundServices } from './types';

export const groupServicesByType = (refundServices: ServicesRefundInfoFragment[]) => {
	const servicesByType = new Map<OrderAdditionalServiceGdsServiceServiceType, ServicesRefundInfoFragment[]>();

	refundServices?.forEach(refundService => {
		const serviceType = refundService?.service?.type;

		servicesByType.set(serviceType, [
			...(servicesByType.has(serviceType) ? servicesByType.get(serviceType) : []),
			refundService
		]);
	});

	return servicesByType;
};

export const groupRefundServicesByPassenger = (refundServices: ServicesRefundInfoFragment[]) => {
	const serviceByPassenger: RefundServices = {};

	refundServices?.forEach(gdsInfo => {
		const service = gdsInfo.service;

		gdsInfo.gdsServiceProductsRefundInfo.forEach(product => {
			setProductToGroup(serviceByPassenger, product, service);
		});
	});

	return serviceByPassenger;
};

const setProductToGroup = (
	group: RefundServices,
	product: ExareGdsServiceProductRefundInfo,
	service: IGdsServiceInfo
) => {
	const passengerId = product.product.passengerId,
		emdNumber = product.product.emdNumber;

	if (group.hasOwnProperty(passengerId)) {
		group[passengerId] = {
			...group[passengerId],
			[emdNumber]: {
				services: {
					...(group[passengerId][emdNumber]?.services || {}),
					[service.id]: {
						service: service,
						products: [...(group[passengerId][emdNumber]?.services?.[service.id]?.products || []), product],
						sumToRefund: {
							amount:
								(group[passengerId][emdNumber]?.services?.[service.id]?.sumToRefund?.amount || 0) +
								product.sumToRefund?.amount,
							currency: product.sumToRefund?.currency
						}
					}
				}
			}
		};
	} else {
		group[passengerId] = {
			[emdNumber]: {
				services: {
					[service.id]: {
						service,
						products: [product],
						sumToRefund: product.sumToRefund
					}
				}
			}
		};
	}
};

export const getProductsToExchange = (services: PassengerSelectedGdsServicesFragment[], segmentIds: string[] = []) => {
	const emdMap = new Map<string, string>();

	const productIdsByEmd = new Map<string, number[]>();

	const productIds: number[] = [];

	services.forEach(service => {
		const isMatchBySegments =
			!segmentIds.length || segmentIds.find(segmentId => service.segmentIds.includes(segmentId));

		service.products.forEach(product => {
			if (product.emdNumber) {
				productIdsByEmd.set(product.emdNumber, [
					...(productIdsByEmd.get(product.emdNumber) || []),
					parseInt(product.id)
				]);

				if (isMatchBySegments) {
					service.products.forEach(product => {
						emdMap.set(product.emdNumber, product.emdNumber);
					});
				}
			}
		});
	});

	emdMap.forEach(emd => {
		if (productIdsByEmd.has(emd)) {
			productIds.push(...productIdsByEmd.get(emd));
		}
	});

	return [...new Set(productIds)];
};

export const flattenAllowedSegments = (allowedSegments: IGdsServiceInfo['allowedSegments']): string[] => {
	const segmentIds = new Set<string>();

	allowedSegments?.forEach(group => group?.forEach(segmentId => segmentId && segmentIds.add(segmentId)));

	return [...segmentIds];
};

export const getPassengersSelectedProductIds = (
	travellers: PassengerFragment[],
	passengerIds: string[],
	segmentIds: string[],
	isAllFlightToExchange?: boolean
) => {
	const services: PassengerSelectedGdsServicesFragment[] = [];

	passengerIds.forEach(passengerId => {
		const traveller = travellers.find(traveller => traveller.id === passengerId);

		if (traveller?.services?.gdsServices?.services) {
			services.push(...traveller?.services?.gdsServices?.services);
		}

		if (traveller?.services?.seats) {
			traveller.services.seats.forEach(seat => {
				if (!seat.product) {
					return;
				}

				services.push({
					segmentIds: [seat.segment.id],
					products: [seat.product]
				} as PassengerSelectedGdsServicesFragment);
			});
		}
	});

	return getProductsToExchange(services, isAllFlightToExchange ? [] : segmentIds);
};
